import { useEffect, useState } from 'react';
import { useShell, useShellMenus } from '../shell/context';
import type { Menu, MenuItem } from '../shell/TopBar';
import { roundWords } from '../shell/panels/PhaseList';
import { ConfirmDialog } from '../shell/Dialogs';
import { useTableState } from '../table/TableState';
import { useEffectsEndedNotice } from '../table/effectsEnded';
import { useMayAct } from '../table/mayAct';
import { useRemoveObjects } from '../table/strandedCombatants';
import { BoardCanvas } from '../board/BoardCanvas';
import { boardBus } from '../board/bus';
import { EMPTY_BOARD_PX, EMPTY_GRID, dmEncounterView, memberLabel, type DmObject, type Encounter, type PlayerEncounter, type TableMember } from '@dragons-table/vtt-shared';
import { partialGroupNote } from '../shell/panels/PropertiesPanel';
import { FocusArrows, FocusComposer, FocusNote } from '../board/FocusOverlays';
import { PinMarkers } from '../board/PinOverlays';
import { BlastAimLayer } from '../board/BlastOverlays';
import { RolledRestDialog } from '../shell/panels/RolledRestParts';
import { DialogueBubbles } from '../board/DialogueBubbles';
import { AtlasCrumbs, LinkMarkers } from '../board/LinkOverlays';
import { Dice3DLayer } from '../board/DiceTray';
import { WeatherOverlay } from '../board/WeatherOverlay';
import { AreaComposer, DragReadout, RulerReadout } from '../board/AreaOverlays';
import { MovePrompt } from '../board/MoveOverlays';
import { FogComposer, FogGate } from '../board/FogOverlays';
import { ZoneComposer, ZoneGate } from '../board/ZoneOverlays';
import { zoneTool } from '../board/zoneTool';
import { WallComposer, WallGate } from '../board/WallOverlays';
import { wallTool } from '../board/wallTool';
import { useBoardMap, useStaging } from '../board/sceneMap';
import { goLive } from '../table';
import { PerfReadout } from '../board/PerfReadout';
import { fogTool } from '../board/fogTool';
import { areaTool } from '../board/areaTool';
import { drawTool } from '../board/drawTool';
import { DrawTextInput, DrawToolbar, DrawingsList } from '../board/DrawOverlays';
import { measureTool } from '../board/measureTool';
import { focusTool } from '../board/focusTool';
import { TokenMenu } from '../board/TokenMenu';
import { UnlinkedNotice } from '../shell/panels/UnlinkedNotice';
import { ActionBar } from '../board/ActionBar';
import { TokenHoverCard } from '../board/TokenHoverCard';
import { FloorPicker } from '../board/FloorPicker';
import { WikiCard, WikiPickerGate } from '../board/WikiOverlays';
import { ClearFightGate } from '../board/ClearFight';
import { PlaceGate } from '../shell/panels/PlaceDialog';

/**
 * A delete, from the canvas (the Delete key) or a panel, confirmed in one
 * place when the person wants to be asked (design/12 §5), then one event
 * for everything selected — one action, one undelete.
 */
function DeleteGate() {
  const { settings } = useShell();
  const { store } = useTableState();
  // The removal, and the offer to take a combatant it leaves tokenless out of the turn order (issue 1537).
  const remove = useRemoveObjects();
  const mayRemove = useMayAct()('object.remove');
  const [asking, setAsking] = useState<string[] | null>(null);
  useEffect(() => boardBus.onDelete((ids) => {
    if (!mayRemove || ids.length === 0) return;
    if (settings.confirmDelete) setAsking(ids);
    else remove(ids);
  }), [settings.confirmDelete, remove, mayRemove]);
  if (!asking) return null;
  const names = asking.map((id) => store.state.objects[id]?.name).filter(Boolean);
  return (
    <ConfirmDialog title={asking.length === 1 ? `Delete ${names[0] ?? 'this'}?` : `Delete ${asking.length} things?`} confirmLabel="Delete" destructive
      body={<p>{asking.length === 1 ? 'It leaves the board; its picture stays in the library.' : `${names.join(', ')} leave the board; their pictures stay in the library.`} One undelete brings back the last deletion, until your next action.</p>}
      onConfirm={() => { remove(asking); setAsking(null); }} onClose={() => setAsking(null)} />
  );
}

/**
 * Reveal or hide, from the canvas (R and H) or a panel: one event for
 * everything asked, so an ambush lands together, and the reminder when
 * that is part of a numbered group (design/22 Q2) — a note, never a stop.
 */
function RevealGate() {
  const { act, store, role, setNotice } = useTableState();
  // Which are hidden is the DM's board's (`role`); revealing is the `tokens` power's (issue 1160).
  const mayReveal = useMayAct()('object.reveal');
  useEffect(() => boardBus.onReveal((ids, reveal) => {
    if (role !== 'dm' || !mayReveal) return;
    const objects = Object.values(store.state.objects) as DmObject[];
    const wanted = ids.filter((id) => { const o = objects.find((x) => x.id === id); return o && o.hidden === reveal; });
    if (wanted.length === 0) return;
    if (!reveal) { act({ kind: 'object.hide', ids: wanted }); return; }
    act({ kind: 'object.reveal', ids: wanted }, (reply) => { if (reply.ok) { const note = partialGroupNote(objects, wanted); if (note) setNotice(note); } });
  }), [act, store, role, mayReveal, setNotice]);
  return null;
}

/**
 * The centre of the main window (slice 06): the Pixi board, with the
 * notices the table owes everyone laid over it — disconnected across the
 * whole surface (design/21 §5.1), the DM-away status, a refusal in words —
 * and the one control that belongs on the board itself, fit to screen,
 * which the View menu also offers. What is on the board comes from the
 * projected store; the placeholder markers of slice 04 are drawn on it and
 * listed in the properties panel.
 */
/** A wiki card on a token (issue 1155): the card, for everyone; the DM's page picker. */
function WikiGates() {
  const { campaignId } = useShell();
  const { store, role, viewingAs, act } = useTableState();
  const mayAct = useMayAct();
  return (
    <>
      <WikiCard campaignId={campaignId} />
      {role === 'dm' && mayAct('object.wiki') && !viewingAs && <WikiPickerGate campaignId={campaignId} act={act} currentOf={(id) => (store.state.objects[id] as DmObject | undefined)?.wiki?.pageId ?? null} />}
    </>
  );
}

/**
 * Placing the table (issue 811): the DM's reminder that the players are
 * looking at the board as it was, and the one button that shows them — the
 * reminder for anyone on the DM's board, the button for whoever may place.
 */
function PlacingBanner() {
  const { store, role, viewingAs, act } = useTableState();
  const mayPlace = useMayAct()('placement.set');
  // The status is the DM's board's, for anyone on it; only the button follows the power (the review of 2026-10-10:
  // a co-DM seeing the DM's board without `tokens` lost the status too).
  if (role !== 'dm' || viewingAs || store.state.placing !== true) return null;
  const waiting = Object.values(store.state.objects).filter((o) => 'held' in o && o.held).length;
  return (
    <div data-testid="placing-banner" role="status" className="pointer-events-auto flex items-center gap-3 self-start rounded border border-amber-700 bg-amber-900/90 px-3 py-1 text-sm text-amber-100">
      <span>Placing the table — players see nothing you move or add until you are done.{waiting ? ` ${waiting === 1 ? '1 thing waits' : `${waiting} things wait`} to be shown.` : ''}</span>
      {mayPlace && <button type="button" className="shrink-0 whitespace-nowrap rounded border border-amber-200/60 px-2 py-0.5 text-xs hover:bg-amber-800" onClick={() => act({ kind: 'placement.set', on: false })}>Done placing</button>}
    </div>
  );
}

/** The DM staging a scene the players are not on (issue 834): said, with the one button that shows it to them. Low on the board with the placing banner: the top left is where new things land. */
function StagingBanner() {
  const { campaignId, onResult } = useShell();
  const staging = useStaging();
  if (!staging) return null;
  return (
    <div data-testid="staging-banner" role="status" className="pointer-events-auto flex items-center gap-3 self-start rounded bg-violet-900/90 px-3 py-1 text-sm text-violet-50">
      <span>Staging <strong>{staging.staged.name}</strong> — the players are on <strong>{staging.live.name}</strong> and see none of this.</span>
      <button type="button" className="shrink-0 rounded border border-violet-200/60 px-2 py-0.5 text-xs hover:bg-violet-800" onClick={async () => onResult(await goLive(campaignId, staging.staged.id))}>Go live</button>
    </div>
  );
}

/**
 * Which scene this DM window shows, always (issue 840): each DM window has its own, and a second window on the live
 * scene otherwise looks exactly like the one staging. The DM's alone, and only when the table has more than one scene.
 */
function SceneLabel() {
  const { table } = useShell();
  const { scene, role } = useTableState();
  if (role !== 'dm' || !scene || !table.scenes || table.scenes.length < 2) return null;
  const here = table.scenes.find((s) => s.id === scene.id);
  if (!here) return null;
  return (
    // The scene's name in the frame's gold Cinzel (issue 1541); whether the players are on it keeps its colour.
    <span data-testid="scene-label" className="vtt-chrome rounded-[3px] border border-vtt-line bg-vtt-chrome/[0.88] px-2.5 py-1">
      <span className="sr-only">Scene: </span>
      <span className="font-display text-xs font-semibold tracking-[0.05em] text-vtt-gold">{here.name}</span>
      {/* Its own small tag, in fixed light colours on the dark frame (slice 2): the light theme's turned-over greens
          and violets are for its cream panels, and read under 3:1 inside the frame, which stays dark. */}
      <span aria-hidden="true"> · </span>
      <span className={`rounded-sm px-1 text-[0.625rem] font-semibold uppercase tracking-[0.06em] ${here.live ? 'vtt-tag-live' : 'vtt-tag-staged'}`} data-testid="scene-label-state">{here.live ? 'live' : 'staged'}</span>
    </span>
  );
}

/**
 * Whether one of the board's tool bars is open (issue 853): the focus composer, the area composer, the ruler's readout,
 * the fog bar. The tools are exclusive, so at most one is. The fog bar is the DM's own, never while looking through a
 * player's eyes — the same condition FogComposer renders on, so the strip never opens empty.
 */
function useToolOpen(): boolean {
  const { role, viewingAs } = useTableState();
  const read = () => ({ focus: focusTool.get().on, area: areaTool.get().on && !areaTool.get().cast, ruler: measureTool.get().on, fog: fogTool.get().on, draw: drawTool.get().on, wall: wallTool.get().on, zone: zoneTool.get().on });
  const [on, setOn] = useState(read);
  useEffect(() => {
    const again = () => setOn(read());
    const offs = [focusTool.subscribe(again), areaTool.subscribe(again), measureTool.subscribe(again), fogTool.subscribe(again), drawTool.subscribe(again), wallTool.subscribe(again), zoneTool.subscribe(again)];
    again();
    return () => { for (const off of offs) off(); };
  }, []);
  return on.focus || on.area || on.ruler || ((on.fog || on.wall || on.zone) && role === 'dm' && !viewingAs) || (on.draw && !viewingAs);
}

/**
 * The status pill for whoever runs the table (the 2026-10-15 session pass): how many of the seated players are here,
 * and who is not. "The DM is here." told the DM only about themselves, and a player's arrival was said nowhere but the
 * People tool. The pill lets the pointer through (issue 1428), so it says it in words, not on hover. Null with nobody seated.
 */
export function playersHereLine(members: readonly TableMember[], objects: Record<string, unknown>): string | null {
  const players = members.filter((m) => m.role === 'player');
  if (players.length === 0) return null;
  const away = players.filter((m) => !m.present);
  const here = players.length - away.length;
  const count = here === players.length ? `All ${players.length === 1 ? 'one player is' : `${players.length} players are`} here.` : `${here} of ${players.length} players here`;
  return away.length && here < players.length ? `${count} — not here: ${away.map((m) => memberLabel(m, objects as never)).join(', ')}.` : count;
}

/**
 * The pill's own words for the players here (issue 1541, slice 2): the count alone, so the pill stays one short line as
 * the mock-up's does. The whole line, with everyone not here by name, is `playersHereLine`: the pill's title, and read out
 * with it. Null with nobody seated.
 */
export function playersHereShort(members: readonly TableMember[]): string | null {
  const players = members.filter((m) => m.role === 'player');
  if (players.length === 0) return null;
  const here = players.filter((m) => m.present).length;
  if (here < players.length) return `${here} of ${players.length} players here`;
  return players.length === 1 ? 'The one player is here' : `All ${players.length} players here`;
}

/**
 * The round and whose turn it is, for the status pill (issue 1541), from this viewer's reading of the order: the DM's
 * whole, a player's as projected — a turn they cannot see has no active combatant, and a withheld name has no name, so
 * the pill says the round and nothing more. Null with no encounter running.
 */
export function encounterLine(reading: PlayerEncounter, unit?: string): string | null {
  if (!reading.running) return null;
  const active = reading.active ? reading.combatants.find((c) => c.id === reading.active) : undefined;
  return active?.name ? `${roundWords(reading, unit)} · ${active.name}'s turn` : roundWords(reading, unit);
}

/**
 * The board's tool column (issue 1541; design/07 §2): shortcuts to the menu items that turn on a board mode, never the
 * only way to them. Each runs the item's own handler and shows its tick; an item the menus do not offer this person
 * (areas without distance rules, grid setup without the map) has no shortcut. Move and select is no mode at all: it
 * is on while every mode is off, and choosing it turns off the one that is on, through that item's handler.
 */
export const BOARD_SHORTCUTS = [
  { id: 'select', label: 'Move and select' },
  { id: 'measure', label: 'Measure distance' },
  { id: 'area', label: 'Lay an area template' },
  { id: 'draw', label: 'Draw on the map' },
  { id: 'grid', label: 'Grid setup' },
] as const;
/** The menu items that are a board mode: Move and select is on while none of them is. */
const BOARD_MODES = ['point', 'whats-this', 'measure', 'area', 'fog', 'walls', 'zones', 'grid', 'draw'];
type ActionItem = Extract<MenuItem, { kind: 'action' }>;
const actionsOf = (menus: Menu[]): ActionItem[] => menus.filter((m) => !m.custom).flatMap((m) => m.items).filter((i): i is ActionItem => i.kind === 'action');

function ShortcutIcon({ id }: { id: (typeof BOARD_SHORTCUTS)[number]['id'] }) {
  const common = { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, 'aria-hidden': true } as const;
  if (id === 'select') return <svg {...common}><path d="M8 1.5v13M1.5 8h13M8 1.5 6 3.5M8 1.5l2 2M8 14.5l-2-2M8 14.5l2-2M1.5 8l2-2M1.5 8l2 2M14.5 8l-2-2M14.5 8l-2 2" /></svg>;
  if (id === 'measure') return <svg {...common}><path d="M2 11 11 2l3 3-9 9z" /><path d="M5 8l1.5 1.5M7 6l1.5 1.5M9 4l1.5 1.5" /></svg>;
  if (id === 'area') return <svg {...common}><circle cx="8" cy="8" r="6" /><circle cx="8" cy="8" r="2.5" /></svg>;
  if (id === 'draw') return <svg {...common}><path d="M3 13l1-3.5L11 2.5 13.5 5 6.5 12z" /><path d="M9.5 4l2.5 2.5" /></svg>;
  return <svg {...common}><path d="M2 2h12v12H2zM6 2v12M10 2v12M2 6h12M2 10h12" /></svg>;
}

export function BoardToolColumn() {
  const menus = useShellMenus();
  const items = actionsOf(menus);
  const item = (id: string) => items.find((i) => i.id === id);
  const modes = items.filter((i) => BOARD_MODES.includes(i.id) && i.checked);
  const shown = BOARD_SHORTCUTS.filter((s) => s.id === 'select' || item(s.id));
  const { phone } = useShell();
  if (shown.length <= 1) return null;
  return (
    // In the phone layout (issue 1242) the column runs on into a second one on a short board; only its buttons take a press.
    <div role="toolbar" aria-label="Map tools" aria-orientation="vertical" data-testid="board-tool-column"
      className={`vtt-chrome absolute z-20 flex flex-col gap-1 ${phone ? 'pointer-events-none inset-y-1 left-1 flex-wrap content-start [&>button]:pointer-events-auto' : 'pointer-events-auto left-3 top-3'}`}>
      {shown.map((s) => {
        const it = s.id === 'select' ? null : item(s.id)!;
        const on = it ? it.checked === true : modes.length === 0;
        const disabled = it ? Boolean(it.disabled) : false;
        return (
          <button key={s.id} type="button" aria-label={it?.label ?? s.label} aria-pressed={on} disabled={disabled} title={it?.label ?? s.label}
            data-testid={`board-tool-${s.id}`}
            className={`grid h-[2.125rem] w-[2.125rem] place-items-center rounded-[3px] border bg-vtt-chrome/[0.88] disabled:opacity-40 ${on ? 'border-vtt-gold text-vtt-gold' : 'border-vtt-line text-vtt-soft hover:text-vtt-fg'}`}
            onClick={() => { if (it) { if (!it.disabled) it.onSelect(); } else for (const m of modes) if (!m.disabled) m.onSelect(); }}>
            <ShortcutIcon id={s.id} />
          </button>
        );
      })}
    </div>
  );
}

export function Board() {
  const { status, notice, noticeOpen, connected, viewingAs, viewAs, role, store, setNotice, systemRules } = useTableState();
  const { table, phone } = useShell();
  const map = useBoardMap();
  const toolOpen = useToolOpen();
  useEffectsEndedNotice();
  return (
    <div className="flex h-full w-full flex-col" data-testid="board">
      {/* The tool bars have a strip of their own ABOVE the board (issue 853): laid over it, the fog bar covered the map's
          top rows and a press there hit the bar, not the board. Only while a tool is open; the canvas resizes to what is left. */}
      {toolOpen && (
        <div className="flex shrink-0 flex-col items-start gap-1 border-b border-stone-800 bg-stone-950 p-2" data-testid="board-tools">
          <FocusComposer />
          <AreaComposer />
          <RulerReadout />
          <FogComposer />
          <WallComposer />
          <ZoneComposer />
          <DrawToolbar />
        </div>
      )}
      {/* A line round the canvas (issue 1541), the board framed like the bars beside it. */}
      {/* The hot bar (qa-runs#369) over the board, for a person who set it there; under it otherwise (below). */}
      {!viewingAs && <ActionBar where="top" />}
      <div className="relative min-h-0 w-full flex-1 rounded-[3px] border border-vtt-line" data-testid="board-area">
        <BoardCanvas />
        {/* The scene's weather (issue 1163): a faint veil and its name, under every control. */}
        <WeatherOverlay />
        <DrawTextInput />
        <DrawingsList />
        <DeleteGate />
        <RevealGate />
        <FogGate />
        <WallGate />
        <ZoneGate />
        <TokenMenu />
        <TokenHoverCard />
        <FloorPicker />
        <WikiGates />
        <ClearFightGate />
        <PlaceGate />
        {/* The edge arrows sit above the notice pills and the map tools' column (issue 1541): an arrow in a corner must stay clickable (found on vtt-dev, where the status pill, then the tools, covered it). */}
        <div className="pointer-events-none absolute inset-0 z-30"><FocusArrows /></div>
        {/* Map pins (issue 1154): the ones this person was sent; a click opens the handout, or the DM's note. */}
        <div className="pointer-events-none absolute inset-0 z-10"><PinMarkers /></div>
        <div className="pointer-events-none absolute inset-0 z-20"><BlastAimLayer /></div>
        <RolledRestDialog />
        <div className="pointer-events-none absolute inset-0 z-10"><DialogueBubbles /></div>
        {/* Scene links (issue 1173): the DM's every one; a player's only those shown. */}
        <div className="pointer-events-none absolute inset-0 z-10"><LinkMarkers /></div>
        {/* 3D dice (issue 1183): each person's own choice; a roll they were sent lands on its faces. */}
        <Dice3DLayer />
        <FocusNote />
        <PerfReadout />
        <BoardToolColumn />
        {/* The notices at the top CENTRE (issue 837): at the top left they covered square 1-1, where "Add a marker" places things. */}
        {/* Clear of the tool column at the top left (issue 1541): a long presence line wraps rather than running under it. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex flex-col items-center gap-1 px-14 py-2" data-testid="board-notices">
          {!connected && (
            <div role="alert" data-testid="disconnected" className="pointer-events-auto rounded bg-red-900/80 p-3 text-red-100">
              {/* The socket reconnects on its own (session.ts); there is nothing for the person to do but wait. */}
              Disconnected — reconnecting… The table is not current, and nothing you do now is sent until it is back.
            </div>
          )}
          {viewingAs && (
            <div data-testid="viewing-as" className="pointer-events-auto flex items-center gap-3 rounded bg-sky-900/90 px-3 py-1 text-sm text-sky-100">
              <span>Viewing as {viewingAs.label ?? (viewingAs.handle ? `@${viewingAs.handle}` : 'a person without a handle')} — what they cannot see is not here.</span>
              <button type="button" className="rounded border border-sky-300/60 px-2 py-0.5 text-xs hover:bg-sky-800" onClick={() => viewAs(null)}>Back to your view</button>
            </div>
          )}
          {/* A drag's cost (issue 1151) over the board, not in the tool strip: a plain drag opens no tool, and a strip appearing mid-drag would move the canvas under the hand. */}
          <DragReadout />
          {/* The kind of move, what is left of a Move, and the confirmation a drop waits on (qa-runs#1161). */}
          <MovePrompt />
          {/* Text only, so the pointer passes through (issue 1428): a click under "The DM is here." reaches the square, and a drag does not select its words. */}
          {(() => {
            const players = role === 'dm' && !viewingAs ? playersHereLine(table.members, store.state.objects) : null;
            const short = role === 'dm' && !viewingAs ? playersHereShort(table.members) : null;
            const presence = short ?? (status.dmPresent ? 'The DM is here.' : status.viewOnly ? 'The DM is away: the table is view-only.' : 'The DM stepped away; the table stays live for a moment.');
            // Who is not here, by name: the pill's title, and read out after the count (the eye has the short line).
            const away = players && short && players !== short && players.startsWith(short) ? players.slice(short.length) : null;
            // The fight as this person's store holds it (issue 1541): the DM's order seen whole, a player's as projected.
            const raw = store.state.encounter as Encounter | PlayerEncounter | undefined;
            const fight = raw ? encounterLine(role === 'dm' ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter), systemRules.units[0]?.label) : null;
            const settled = players ? table.members.every((m) => m.role !== 'player' || m.present) : status.dmPresent;
            return (
              // The frame's pill (issue 1541): chrome at 88% with a gold-dim edge, presence first, then the round and the turn.
              // Short (slice 2): the count, then the fight; the names of who is not here are its title and its spoken words.
              // The pill lets the pointer through (issue 1428) save the count while someone is away, so the title can show.
              <p className="vtt-chrome pointer-events-none flex select-none items-center gap-2 rounded-full border border-vtt-gold-dim bg-vtt-chrome/[0.88] px-3.5 py-1 text-xs text-vtt-fg" data-testid="table-status"
                title={away ? players! : undefined}>
                <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${settled ? 'bg-emerald-500' : 'bg-vtt-gold'}`} />
                <span>
                  <span data-testid="table-status-count" className={away ? 'pointer-events-auto cursor-help' : ''}>{presence}</span>
                  {away && <span className="sr-only" data-testid="table-status-away">{away}</span>}
                  {fight && <span data-testid="table-status-fight"> · {fight}</span>}
                </span>
              </p>
            );
          })()}
          {/* Spoken by the shell's live region (issue 1175), so it is not an alert of its own as well. */}
          {notice && (
            <p data-testid="board-notice" className="pointer-events-none select-none rounded bg-stone-900/80 px-2 py-0.5 text-sm text-amber-300">
              {notice}
              {/* A notice that names a tool opens it (a handout sent: "Open Handouts"), clickable though the pill is not; one
                  that offers an act does it and goes (issue 1537). */}
              {noticeOpen && <button type="button" className="pointer-events-auto ml-2 rounded border border-amber-600 px-1.5 text-xs hover:bg-stone-800" onClick={() => { if ('run' in noticeOpen) { noticeOpen.run(); setNotice(''); } else boardBus.showTool(noticeOpen.tool); }}>{noticeOpen.label}</button>}
              {/* It stays a minute (NOTICE_ACTION_MS), so it can be put away sooner. */}
              {noticeOpen && <button type="button" className="pointer-events-auto ml-1 rounded px-1 text-xs text-stone-400 hover:bg-stone-800" aria-label="Dismiss" title="Dismiss" onClick={() => setNotice('')}>×</button>}
            </p>
          )}
        </div>
        {/* Low on the board, not with the notices: at the top left it pushed the status pill onto where new tokens land; on the
            bottom row it ran under the map pill and Fit to screen (both found on vtt-dev). One row up, it covers neither. */}
        <div className="pointer-events-none absolute bottom-10 left-2 right-2 flex flex-col items-start gap-1"><AtlasCrumbs /><StagingBanner /><PlacingBanner /></div>
        {/* Clear of the tool column (slice 2): on a small board at 200% text the three wrap upward from the right instead of
            running over the column. The row lets the pointer through but for Fit to screen. */}
        <div className="pointer-events-none absolute bottom-2 left-[4.5rem] right-2 flex flex-wrap items-center justify-end gap-2 text-xs text-stone-300">
          {/* In the phone layout (issue 1242) the board keeps its room: the map's name and size wait for a larger screen. */}
          {!phone && <SceneLabel />}
          {/* The squares the grid has, counted as Grid setup counts them (qa-runs 138): a 40-across grid reads 40, not 30. */}
          <span className={`vtt-chrome rounded-[3px] border border-vtt-line bg-vtt-chrome/[0.88] px-2.5 py-1 text-vtt-soft ${phone ? 'hidden' : ''}`} data-testid="map-info">
            {map ? `${map.name} · ${map.width} × ${map.height}` : `Empty grid · ${Math.round(EMPTY_BOARD_PX / (store.state.grid?.x.basePitch ?? EMPTY_GRID.squarePx))} × ${Math.round(EMPTY_BOARD_PX / (store.state.grid?.y.basePitch ?? EMPTY_GRID.squarePx))} squares`}
          </span>
          <button type="button" className="vtt-chrome pointer-events-auto rounded-[3px] border border-vtt-line bg-vtt-chrome/[0.88] px-2.5 py-1 text-vtt-soft hover:border-vtt-gold-dim hover:text-vtt-fg" onClick={() => boardBus.fit()}>
            Fit to screen
          </button>
        </div>
      </div>
      {/* The hot bar (qa-runs#369; the action bar of issue 1130) has a strip of its own UNDER the board, sidebar to sidebar
          and as wide as the board, like the tool bars above it: laid over the board it would cover the bottom rows a tap
          must reach and the corner buttons (Fit to screen, the map name), which stay above it. */}
      {!viewingAs && <UnlinkedNotice />}
      {!viewingAs && <ActionBar where="bottom" />}
    </div>
  );
}
